import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Upload, Search, Filter, Eye, Archive, ArchiveRestore, MoreVertical } from 'lucide-react';
import { Table } from '../components/ui/Table';
import { driverApi, DriverUser, Partner } from '../services/driverApi';
import { ImportUsersModal } from '../components/driver/ImportUsersModal';
import { CreateUserModal } from '../components/driver/CreateUserModal';
import { UserDetailsModal } from '../components/driver/UserDetailsModal';
import { AddEsimModal } from '../components/driver/AddEsimModal';
import { TopUpModal } from '../components/driver/TopUpModal';

export const DriverUsers: React.FC = () => {
  const [users, setUsers] = useState<DriverUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    hasActiveEsim: 'all',
    nextDue: 'all',
    partner: 'all'
  });
  const [selectedUser, setSelectedUser] = useState<DriverUser | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showAddEsim, setShowAddEsim] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const extractPartnersFromUsers = useCallback((users: DriverUser[]) => {
    const partnerMap = new Map<string, Partner>();

    users.forEach(user => {
      if (user.partner) {
        partnerMap.set(user.partner.name, user.partner);
      }
    });

    const uniquePartners = Array.from(partnerMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    setPartners(uniquePartners);
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        page_size: pagination.page_size
      };

      if (filters.search) params.search = filters.search;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.partner !== 'all') params.partner = filters.partner;
      if (filters.hasActiveEsim !== 'all') params.hasActiveEsim = filters.hasActiveEsim;
      if (filters.nextDue !== 'all') params.nextDue = filters.nextDue;

      const response = await driverApi.getUsers(params);
      console.log('DriverUsers API response:', response);
      setUsers(response.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        page_length: response.pagination?.page_size || prev.page_size
      }));

      // Extract partners from users data
      extractPartnersFromUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, filters, extractPartnersFromUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        activeDropdown &&
        !(
          target.closest('.dropdown-container') ||
          target.closest('.dropdown-menu') ||
          target.closest('.dropdown-trigger')
        )
      ) {
        setActiveDropdown(null);
        setMenuPosition(null);
      }
    };

    const handleWindowChange = () => {
      if (activeDropdown) {
        // Close menu on scroll/resize to avoid misalignment
        setActiveDropdown(null);
        setMenuPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleWindowChange, true);
    window.addEventListener('resize', handleWindowChange);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleWindowChange, true);
      window.removeEventListener('resize', handleWindowChange);
    };
  }, [activeDropdown]);

  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      hasActiveEsim: 'all',
      nextDue: 'all',
      partner: 'all'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleArchiveUser = async (user: DriverUser) => {
    try {
      await driverApi.archiveUser(user.name);
      await loadUsers();
    } catch (error) {
      console.error('Failed to archive user:', error);
    }
  };

  const handleUnarchiveUser = async (user: DriverUser) => {
    try {
      await driverApi.unarchiveUser(user.name);
      await loadUsers();
    } catch (error) {
      console.error('Failed to unarchive user:', error);
    }
  };

  const handleViewUser = (user: DriverUser) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleAddEsim = (user: DriverUser) => {
    setSelectedUser(user);
    setShowAddEsim(true);
    setActiveDropdown(null);
  };

  const handleTopUp = (user: DriverUser) => {
    setSelectedUser(user);
    setShowTopUp(true);
    setActiveDropdown(null);
  };

  const handleDisableAutoRenew = (user: DriverUser) => {
    // TODO: Implement disable auto-renew functionality
    console.log('Disable auto-renew for user:', user.name);
    setActiveDropdown(null);
  };

  const handleViewDetails = (user: DriverUser) => {
    handleViewUser(user);
    setActiveDropdown(null);
  };

  const handleArchive = (user: DriverUser) => {
    handleArchiveUser(user);
    setActiveDropdown(null);
  };

  const handleUnarchive = (user: DriverUser) => {
    handleUnarchiveUser(user);
    setActiveDropdown(null);
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      ACTIVE: 'bg-green-100 text-green-800',
      ARCHIVED: 'bg-gray-100 text-gray-800',
      PENDING: 'bg-blue-100 text-blue-800',
      NOT_ACTIVE: 'bg-purple-100 text-purple-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const columns = [
    {
      key: 'name',
      header: 'NAME',
      render: (value: any, row: DriverUser) => (
        <div 
          onClick={() => handleViewDetails(row)}
          className="text-blue-600 hover:text-blue-800 cursor-pointer"
        >
          {row.first_name} {row.last_name}
        </div>
      )
    },
    {
      key: 'email',
      header: 'EMAIL',
      render: (value: any, row: DriverUser) => (
        <div 
          onClick={() => handleViewDetails(row)}
          className="text-blue-600 hover:text-blue-800 cursor-pointer"
        >
          {row.email}
        </div>
      )
    },
    {
      key: 'phone',
      header: 'PHONE',
      render: (value: any, row: DriverUser) => (
        <div className="text-sm text-gray-900">{row.phone || '-'}</div>
      )
    },
    {
      key: 'status',
      header: 'STATUS',
      render: (value: any, row: DriverUser) => getStatusBadge(row.status || 'ACTIVE')
    },
    {
      key: 'partner',
      header: 'PARTNER',
      render: (value: any, row: DriverUser) => (
        <div className="text-sm text-gray-900">
          {row.partner ? (
            <div>
              <div className="font-medium">{row.partner.name}</div>
            </div>
          ) : '-'}
        </div>
      )
    },
    {
      key: 'subscription',
      header: 'CURRENT SUBSCRIPTION',
      render: (value: any, row: DriverUser) => {
        if (row.item_code && row.profile_status === 'ACTIVE') {
          // Get status from esim_profile.usage.status (ESIM Usage status from Airalo API) if available
          const usageStatus = row.esim_profile?.usage?.status;
          const status = usageStatus || row.profile_status || 'ACTIVE';
          
          // Determine badge color based on status
          const statusClasses: { [key: string]: string } = {
            'active': 'bg-green-100 text-green-800',
            'inactive': 'bg-gray-100 text-gray-800',
            'not_active': 'bg-gray-100 text-gray-800',
            'suspended': 'bg-yellow-100 text-yellow-800',
            'expired': 'bg-red-100 text-red-800',
            'pending': 'bg-blue-100 text-blue-800',
            'failed': 'bg-red-100 text-red-800',
            'canceled': 'bg-gray-100 text-gray-800',
            'requested': 'bg-yellow-100 text-yellow-800',
            'ACTIVE': 'bg-green-100 text-green-800',
            'INACTIVE': 'bg-gray-100 text-gray-800',
            'NOT_ACTIVE': 'bg-gray-100 text-gray-800',
            'SUSPENDED': 'bg-yellow-100 text-yellow-800',
            'EXPIRED': 'bg-red-100 text-red-800',
            'PENDING': 'bg-blue-100 text-blue-800',
            'FAILED': 'bg-red-100 text-red-800',
            'CANCELED': 'bg-gray-100 text-gray-800',
            'REQUESTED': 'bg-yellow-100 text-yellow-800',
            'Pending': 'bg-blue-100 text-blue-800',
            'Active': 'bg-green-100 text-green-800',
            'Not Active': 'bg-gray-100 text-gray-800',
            'Suspended': 'bg-yellow-100 text-yellow-800',
            'Failed': 'bg-red-100 text-red-800',
            'Canceled': 'bg-gray-100 text-gray-800',
            'Requested': 'bg-yellow-100 text-yellow-800'
          };
          
          const statusClass = statusClasses[status] || 'bg-gray-100 text-gray-800';
          const statusDisplay = status.toUpperCase();
          
          return (
            <div className="text-sm">
              <div className="text-gray-900">{row.item_code}</div>
              <div className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                  {statusDisplay}
                </span>
              </div>
            </div>
          );
        } else {
          return (
            <div className="text-sm">
              <div className="text-gray-500">No active eSIM</div>
              <div className="mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  NONE
                </span>
              </div>
            </div>
          );
        }
      }
    },
    {
      key: 'due_date',
      header: 'NEXT DUE DATE',
      render: (value: any, row: DriverUser) => {
        if (row.due_date && row.profile_status === 'ACTIVE') {
          const dueDate = new Date(row.due_date);
          return (
            <div className="text-sm text-gray-900">
              {dueDate.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </div>
          );
        } else {
          return <div className="text-sm text-gray-500">-</div>;
        }
      }
    },
    {
      key: 'renewal_count',
      header: 'RENEWAL COUNT',
      render: (value: any, row: DriverUser) => {
        const count = row.renewal_count || 0;
        return (
          <div className="text-sm">
            <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${
              count > 0
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {count}
            </span>
          </div>
        );
      }
    },
    {
      key: 'start_date',
      header: 'START DATE',
      render: (value: any, row: DriverUser) => {
        if (row.activation_date && row.profile_status === 'ACTIVE') {
          // Use activation_date from ESIM Profile as Start Date
          const activationDate = new Date(row.activation_date);
          return (
            <div className="text-sm text-gray-900">
              {activationDate.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </div>
          );
        } else {
          return <div className="text-sm text-gray-500">-</div>;
        }
      }
    },
    {
      key: 'created',
      header: 'CREATED',
      render: (value: any, row: DriverUser) => {
        const createdDate = new Date(row.created_at);
        return (
          <div className="text-sm text-gray-900">
            {createdDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })}
          </div>
        );
      }
    },
    {
      key: 'actions',
      header: 'ACTIONS',
      render: (value: any, row: DriverUser) => (
        <div className="flex justify-center relative dropdown-container">
          <button
            onClick={(e) => {
              const btn = e.currentTarget as HTMLElement;
              const rect = btn.getBoundingClientRect();
              const menuWidth = 192; // w-48 (12rem)
              const spacing = 8; // 0.5rem
              if (activeDropdown === row.name) {
                setActiveDropdown(null);
                setMenuPosition(null);
              } else {
                setActiveDropdown(row.name);
                setMenuPosition({
                  top: rect.bottom + spacing,
                  left: Math.max(8, rect.right - menuWidth),
                });
              }
            }}
            className="text-gray-400 hover:text-gray-600 p-1 dropdown-trigger"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {activeDropdown === row.name && menuPosition && createPortal(
            <div
              className="dropdown-menu fixed z-50 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
              style={{ top: menuPosition.top, left: menuPosition.left }}
            >
              <div className="py-1" role="menu" aria-orientation="vertical">
                <button
                  onClick={() => handleViewDetails(row)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  View Details
                </button>
                {row.status === 'ACTIVE' ? (
                  <>
                    <button
                      onClick={() => handleAddEsim(row)}
                      className={`block px-4 py-2 text-sm w-full text-left ${
                        row.item_code && row.profile_status === 'ACTIVE'
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      disabled={!!(row.item_code && row.profile_status === 'ACTIVE')}
                    >
                      Add eSIM
                    </button>
                    <button
                      onClick={() => handleTopUp(row)}
                      className={`block px-4 py-2 text-sm w-full text-left ${
                        row.item_code && row.profile_status === 'ACTIVE'
                          ? 'text-gray-700 hover:bg-gray-100'
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      disabled={!(row.item_code && row.profile_status === 'ACTIVE')}
                    >
                      Top-up
                    </button>
                    <button
                      onClick={() => handleDisableAutoRenew(row)}
                      className="block px-4 py-2 text-sm text-gray-400 cursor-not-allowed w-full text-left"
                      disabled
                    >
                      Disable Auto-Renew
                    </button>
                    <button
                      onClick={() => handleArchive(row)}
                      className="block px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                    >
                      Archive
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleUnarchive(row)}
                    className="block px-4 py-2 text-sm text-green-600 hover:bg-gray-100 w-full text-left"
                  >
                    Unarchive
                  </button>
                )}
              </div>
            </div>,
            document.body
          )}
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create User
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        {/* Search Field - Full Width */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Filter Dropdowns - Next Line */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Has Active eSIM</label>
            <select
              value={filters.hasActiveEsim}
              onChange={(e) => handleFilterChange('hasActiveEsim', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Next Due</label>
            <select
              value={filters.nextDue}
              onChange={(e) => handleFilterChange('nextDue', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Any time</option>
              <option value="today">Today</option>
              <option value="week">This week</option>
              <option value="month">This month</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">All Statuses</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">All Partners</label>
            <select
              value={filters.partner}
              onChange={(e) => handleFilterChange('partner', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Partners</option>
              {partners.map((partner) => (
                <option key={partner.code} value={partner.code}>
                  {partner.code ? `${partner.name}` : partner.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={handleResetFilters}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Users Table */}
      <Table
        columns={columns}
        data={users}
        isLoading={loading}
        pagination={pagination}
        rowClassName=""
        cellClassName="py-2 align-middle"
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, page_size: pageSize, page: 1 }))}
        emptyMessage="No users found"
      />

      {/* Modals */}
      {showImportModal && (
        <ImportUsersModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            loadUsers();
          }}
        />
      )}

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={async () => {
            console.log('Create user success, refreshing table...');
            setShowCreateModal(false);
            await loadUsers();
            console.log('Table refresh completed');
          }}
        />
      )}

      {showUserDetails && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => {
            setShowUserDetails(false);
            setSelectedUser(null);
          }}
          onAddEsim={() => {
            setShowUserDetails(false);
            setShowAddEsim(true);
          }}
          onUserUpdate={loadUsers}
        />
      )}

      {showAddEsim && selectedUser && (
        <AddEsimModal
          user={selectedUser}
          onClose={() => {
            setShowAddEsim(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowAddEsim(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}

      {showTopUp && selectedUser && (
        <TopUpModal
          isOpen={showTopUp}
          user={selectedUser}
          currentEsim={{
            item_code: selectedUser.item_code || '',
            due_date: selectedUser.due_date || '',
            renewal_count: selectedUser.renewal_count || 0,
            iccid: selectedUser.iccid || ''
          }}
          onClose={() => {
            setShowTopUp(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowTopUp(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};
